<template>
  <div class="ibox">
    <div class="ibox-title"><h5>私人定制管理</h5></div>
    <div class="ibox-content">
    <div class="crud-bar">
      <el-select v-model="status" placeholder="全部状态" clearable @change="fetch" style="width:160px">
        <el-option label="未支付(1)" value="1" />
        <el-option label="未分配(2)" value="2" />
        <el-option label="制作中(3)" value="3" />
        <el-option label="待审核(4)" value="4" />
        <el-option label="待评估(5)" value="5" />
        <el-option label="已完成(6)" value="6" />
      </el-select>
      <el-button icon="el-icon-refresh" @click="fetch">刷新</el-button>
    </div>
    <el-table :data="list" v-loading="loading" border stripe>
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="name" label="姓名" width="110" />
      <el-table-column prop="tel" label="电话" width="130" />
      <el-table-column label="择期" min-width="190">
        <template slot-scope="s">
          <div v-if="s.row.periods && s.row.periods.length" class="period-list">
            <el-tooltip
              v-for="(p, i) in s.row.periods"
              :key="i"
              placement="top"
              effect="dark"
              append-to-body
              :content="periodTooltip(p)"
            >
              <div class="period-line">
                <span class="period-date">{{ p.title }}</span>
                <span v-if="p.lunar" class="period-lunar">{{ p.lunar }}</span>
              </div>
            </el-tooltip>
          </div>
          <span v-else class="period-empty">—</span>
        </template>
      </el-table-column>
      <el-table-column prop="area" label="区域" width="100" />
      <el-table-column prop="price" label="价格" width="90" />
      <el-table-column label="状态" width="100">
        <template slot-scope="s"><el-tag>{{ statusText(s.row.status) }}</el-tag></template>
      </el-table-column>
      <el-table-column prop="adminId" label="负责人" width="80" />
      <el-table-column label="申请时间" width="170">
        <template slot-scope="s">{{ dateTimeText(s.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="240">
        <template slot-scope="s">
          <el-button size="mini" @click="openDetail(s.row)">详情</el-button>
          <el-button size="mini" type="primary" @click="onAssign(s.row)">分配</el-button>
          <el-button size="mini" type="success" @click="onComplete(s.row)">完成</el-button>
          <el-button size="mini" type="warning" @click="onReturn(s.row)">退回</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="订单详情" :visible.sync="detailVisible" width="640px">
      <table class="detail-table">
        <tbody>
          <tr v-for="row in detailRows" :key="row.key">
            <th>{{ row.label }}</th>
            <td>
              <template v-if="row.type === 'list'">
                <div v-if="row.lines.length" class="period-list">
                  <div v-for="(line, i) in row.lines" :key="i" class="period-line">
                    <span class="period-date">{{ line.title }}</span>
                    <span v-if="line.lunar" class="period-lunar">{{ line.lunar }}</span>
                  </div>
                </div>
                <span v-else class="period-empty">—</span>
              </template>
              <template v-else>{{ row.text }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </el-dialog>
    </div>
  </div>
</template>

<script>
import { sirensApi } from '../api/business'

const STATUS_MAP = { 1: '未支付', 2: '未分配', 3: '制作中', 4: '待审核', 5: '待评估', 6: '已完成' }

// 详情表单的字段顺序与中文名；未在接口返回的字段自动跳过
// 字段顺序与名称对齐 C 端下单页 /tianxing/siren 的表单；
// 其后是后台流转字段（前台表单里没有，统一排在业务字段之后）。
const FIELD_LABELS = [
  ['yongshi', '定制类型'],
  ['smallYongshi', '用事类别'],
  ['fuzhudate', '福主出生日期'],
  ['date', '配合出生日期'],
  ['price', '合计金额'],
  ['zuoshan', '坐山立向'],
  ['area', '用事地点'],
  ['lng', '经度'],
  ['lat', '纬度'],
  ['tel', '联系电话'],
  ['actRange', '用事时间段'],
  ['id', '编号'],
  ['name', '姓名'],
  ['phone', '手机号'],
  ['status', '状态'],
  ['adminId', '负责人ID'],
  ['shenheId', '审核人ID'],
  ['lable', '标签'],
  ['result', '结果'],
  ['createdAt', '申请时间'],
  ['updatedAt', '更新时间']
]

// 后端 sirens.date 有三种形态：纯文本、字面量 "null"、以及 JSON 数组字符串。
// JSON 里的农历字段还存在二次转义（如 \\u6b63\\u6708），需要再解一层，否则表格会出现 JSON / 转义码。
function decodeEscaped (text) {
  if (text === null || text === undefined) return ''
  return String(text).replace(/\\+u([0-9a-fA-F]{4})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
}

// 存量脏数据：部分记录的 birthday 里混进了对象拼接产生的 "[object Object]"
// （如 "1975-12-30-[object Object]"），展示前清掉残渣，避免直接暴露给使用者。
function cleanText (value) {
  return decodeEscaped(value)
    .replace(/\s*[-–—]?\s*\[object [A-Za-z]+\]/g, '')
    .replace(/[-–—\s]+$/, '')
    .trim()
}

function formatPeriod (item) {
  if (!item || typeof item !== 'object') {
    const text = cleanText(item)
    return { title: text, detail: text, lunar: '' }
  }
  const birthday = cleanText(item.birthday)
  const time = cleanText(item.birthTime)
  const shichen = cleanText(item.shicheng)
  const lunarYear = cleanText(item.nyear)
  const lunar = [lunarYear ? `${lunarYear}年` : '', cleanText(item.nyue), cleanText(item.nday)]
    .filter(Boolean)
    .join('')
  return {
    title: [birthday, shichen].filter(Boolean).join(' ') || birthday || '—',
    detail: [birthday, time, shichen].filter(Boolean).join(' '),
    lunar: lunar ? `农历${lunar}` : '',
    nong: cleanText(item.nong)
  }
}

// 后端 /sirens 是 findAll() 原序（主键升序），这里统一按申请时间倒序，
// 保证最新提交的私人定制排在最前面。
function toTimestamp (value) {
  if (!value) return 0
  const time = new Date(String(value).replace(' ', 'T')).getTime()
  return Number.isNaN(time) ? 0 : time
}

// date 存在多种嵌套层级，统一递归展开：
//   1) 普通数组：     [{"birthday":"1980-10-18",...}]
//   2) 数组套字符串： ["{\"nyear\":1980,...}"]   ← id=51 就是这种
//   3) 纯文本 / 字面量 "null" / 空数组
function expandPeriods (value, depth) {
  if (depth > 3 || value === null || value === undefined) return []
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text || text === 'null' || text === '[]' || text === '{}') return []
    if (/^[[{]/.test(text)) {
      try {
        return expandPeriods(JSON.parse(text), depth + 1)
      } catch (e) {
        return [{ title: text, detail: text, lunar: '' }]
      }
    }
    return [{ title: text, detail: text, lunar: '' }]
  }
  if (Array.isArray(value)) {
    return value.reduce((acc, item) => acc.concat(expandPeriods(item, depth + 1)), [])
  }
  if (typeof value === 'object') {
    const period = formatPeriod(value)
    return period.title ? [period] : []
  }
  return []
}

function parsePeriods (raw) {
  return expandPeriods(raw, 0)
}

// 接口返回的 createdAt 形如 2026-11-02T10:27:03，页面展示去掉 T 与毫秒。
function formatDateTime (value) {
  if (!value) return ''
  return String(value).replace('T', ' ').replace(/\.\d+$/, '')
}

// 与 C 端下单页一致：经纬度按「度 分 秒」展示（后端存的是十进制度字符串）
function toDms (value) {
  if (value === null || value === undefined || value === '') return ''
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const sign = num < 0 ? '-' : ''
  const abs = Math.abs(num)
  let deg = Math.floor(abs)
  let min = Math.floor((abs - deg) * 60)
  let sec = Math.round(((abs - deg) * 60 - min) * 60)
  if (sec === 60) { sec = 0; min += 1 }
  if (min === 60) { min = 0; deg += 1 }
  return `${sign}${deg} 度 ${min} 分 ${sec} 秒`
}

export default {
  name: 'AdminSirens',
  data () {
    return {
      list: [],
      loading: false,
      status: '',
      detailVisible: false,
      detailEntity: {}
    }
  },
  computed: {
    // 把详情接口返回的对象转成「中文字段名 → 可读值」的表单行
    detailRows () {
      const entity = this.detailEntity || {}
      const knownKeys = FIELD_LABELS.map(item => item[0])
      // 接口新增但未登记的字段也照常展示，字段名回退为原始 key
      const extraKeys = Object.keys(entity).filter(key => knownKeys.indexOf(key) === -1)
      const fields = FIELD_LABELS
        .filter(([key]) => Object.prototype.hasOwnProperty.call(entity, key))
        .concat(extraKeys.map(key => [key, key]))
      return fields
        .map(([key, label]) => {
          const value = entity[key]
          if (key === 'date' || key === 'fuzhudate') {
            // 详情比列表更详细：带上具体出生时间，农历信息放后面灰色显示
            const lines = parsePeriods(value).map(line => ({
              title: line.detail || line.title,
              lunar: line.lunar
            }))
            return { key, label, type: 'list', lines }
          }
          if (value === null || value === undefined || value === '') {
            return { key, label, type: 'text', text: '—' }
          }
          if (key === 'status') {
            return { key, label, type: 'text', text: STATUS_MAP[value] || String(value) }
          }
          if (key === 'createdAt' || key === 'updatedAt') {
            return { key, label, type: 'text', text: formatDateTime(value) || '—' }
          }
          if (key === 'lng' || key === 'lat') {
            return { key, label, type: 'text', text: toDms(value) || '—' }
          }
          if (key === 'price') {
            const num = Number(value)
            return { key, label, type: 'text', text: Number.isNaN(num) ? String(value) : num.toFixed(2) }
          }
          return { key, label, type: 'text', text: String(value) }
        })
    }
  },
  created () { this.fetch() },
  methods: {
    statusText (s) { return STATUS_MAP[s] || s },
    dateTimeText (value) { return formatDateTime(value) },
    periodTooltip (p) {
      if (!p) return ''
      const main = p.detail || p.title
      return p.lunar ? `${main}（${p.lunar}）` : main
    },
    async fetch () {
      this.loading = true
      try {
        const rows = await sirensApi.list(this.status)
        this.list = (rows || [])
          .map(row => Object.assign({}, row, { periods: parsePeriods(row.date) }))
          .sort((a, b) => (toTimestamp(b.createdAt) - toTimestamp(a.createdAt)) || ((b.id || 0) - (a.id || 0)))
      } catch (e) { this.$message.error(e.uiMessage || e.message) } finally { this.loading = false }
    },
    async openDetail (row) {
      try {
        const d = await sirensApi.detail(row.id)
        this.detailEntity = (d && d.data) ? d.data : (d || {})
        this.detailVisible = true
      } catch (e) { this.$message.error(e.uiMessage || e.message) }
    },
    async onAssign (row) {
      const adminId = await this.$prompt('请输入负责人 admin_id', '分配订单', { inputValue: row.adminId || '' })
        .then(r => r.value).catch(() => null)
      if (adminId === null) return
      try {
        await sirensApi.assign(row.id, Number(adminId))
        this.$message.success('已分配'); this.fetch()
      } catch (e) { this.$message.error(e.uiMessage || e.message) }
    },
    async onComplete (row) {
      if (!await this.$confirm('确认标记为已完成？', '提示', { type: 'warning' }).catch(() => false)) return
      try { await sirensApi.complete(row.id); this.$message.success('已完成'); this.fetch() } catch (e) { this.$message.error(e.uiMessage || e.message) }
    },
    async onReturn (row) {
      const lable = await this.$prompt('退回原因', '退回订单', { inputType: 'textarea' })
        .then(r => r.value).catch(() => null)
      if (lable === null) return
      try { await sirensApi.return(row.id, lable); this.$message.success('已退回'); this.fetch() } catch (e) { this.$message.error(e.uiMessage || e.message) }
    }
  }
}
</script>

<style scoped>
.crud-bar { margin-bottom: 16px; display: flex; gap: 12px; }

.period-list { display: flex; flex-direction: column; gap: 2px; }
.period-line { display: flex; align-items: baseline; gap: 6px; line-height: 20px; cursor: default; min-width: 0; }
.period-date { color: #303133; font-size: 13px; white-space: nowrap; flex: 0 0 auto; }
.period-lunar { color: #909399; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1 1 auto; min-width: 0; }
.period-empty { color: #c0c0c0; }
.detail-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.detail-table th,
.detail-table td { border: 1px solid #ebeef5; padding: 8px 12px; text-align: left; vertical-align: top; line-height: 20px; }
.detail-table th { width: 110px; background: #fafafa; color: #606266; font-weight: 500; white-space: nowrap; }
.detail-table td { color: #303133; word-break: break-all; }
</style>
